import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject, type SyntheticEvent } from "react";
import { activateModal } from "../lib/modal";
import { acquireWarmVideo, releaseVideoToPool } from "../lib/clipPrefetch";
import { enterFull, exitFull, fullElement, onFullChange } from "../lib/fullscreen";

export function Pill({ children }: { children: ReactNode }) {
  return <span className="pill">{children}</span>;
}

export function Dot({ kind = "" }: { kind?: "" | "on" | "ok" | "bad" }) {
  return <span className={`dot ${kind}`} />;
}

export function Bar({ value, label = "Progress" }: { value: number; label?: string }) {
  return (
    <div className="bar" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(Math.max(0, Math.min(1, value)) * 100)}>
      <i style={{ width: `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%` }} />
    </div>
  );
}

export function Seg<T extends string>({ value, options, onChange, labelledBy }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; labelledBy?: string }) {
  return (
    <div className="seg" role="radiogroup" aria-labelledby={labelledBy}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} tabIndex={o.value === value || !options.some(x => x.value === value) && o === options[0] ? 0 : -1} className={o.value === value ? "on" : ""} onClick={() => onChange(o.value)} onKeyDown={(e) => {
          const delta = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
          if (!delta && e.key !== "Home" && e.key !== "End") return;
          e.preventDefault();
          const next = e.key === "Home" ? 0 : e.key === "End" ? options.length - 1 : (options.indexOf(o) + delta + options.length) % options.length;
          onChange(options[next]!.value);
          e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('button[role="radio"]')[next]?.focus();
        }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Check({ on = true, children }: { on?: boolean; children: ReactNode }) {
  return (
    <div className="check">
      <i className={on ? "on" : ""} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

/**
 * The clip / video frame: `src` plays (the working copy with a `#t=` range),
 * `poster` is a real picture of it (the clip's frame at release, or the
 * video's), shown until it plays. Without either, or when the picture fails,
 * it draws the mocks' pitch; the fitted track and ball are drawn only there,
 * because over real footage they would mark a ball that is not in it.
 *
 * `autoPlay` starts `src` when it is shown and again whenever it changes, so
 * choosing a clip is one tap. The video is muted because browsers start a
 * muted video without a tap on it; the working copy has no audio track
 * (worker ingest writes `-an`), so nothing is lost. `playsInline` keeps it in
 * the page on an iPhone instead of opening fullscreen.
 *
 * `fullscreen`: a button in the top left corner takes the whole frame full
 * screen (`lib/fullscreen.ts`), with what `children` draw over the clip; the
 * clip is then shown whole, not cropped to the frame. Chrome's own full
 * screen button is turned off (`controlsList`): it would take the video alone.
 */
export function VidFrame({ height, tag, tr, ball, track, play, src, poster, autoPlay, videoRef, fullscreen, flagged, selected, style, onClick, onContextMenu, onLongPress, children, description }: {
  height: number | string;
  tag?: string | undefined;
  tr?: string | undefined;
  ball?: [number, number] | undefined;
  track?: boolean | undefined;
  play?: boolean | undefined;
  src?: string | null | undefined;
  poster?: string | null | undefined;
  autoPlay?: boolean | undefined;
  videoRef?: RefObject<HTMLVideoElement | null> | undefined;
  fullscreen?: boolean | undefined;
  flagged?: boolean | undefined;
  selected?: boolean | undefined;
  style?: CSSProperties | undefined;
  onClick?: (() => void) | undefined;
  onContextMenu?: (() => void) | undefined;
  onLongPress?: (() => void) | undefined;
  children?: ReactNode;
  description?: string | undefined;
}) {
  const descriptionId = useId();
  const timer = useRef<number | null>(null);
  const frame = useRef<HTMLDivElement | null>(null);
  const own = useRef<HTMLVideoElement | null>(null);
  const video = videoRef ?? own;
  const [failed, setFailed] = useState<string | null>(null);
  const [full, setFull] = useState(false);
  const picture = poster && failed !== poster ? poster : null;
  const real = !!src || !!picture;
  useLayoutEffect(() => {
    const el = frame.current;
    if (!el || !src) {
      video.current = null;
      return;
    }
    const v = acquireWarmVideo(src);
    v.controls = true;
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    if (picture) v.poster = picture;
    else v.removeAttribute("poster");
    if (fullscreen) v.setAttribute("controlsList", "nofullscreen");
    else v.removeAttribute("controlsList");
    el.insertBefore(v, el.firstChild);
    video.current = v;
    if (autoPlay) void v.play().catch(() => {});
    return () => {
      if (video.current === v) video.current = null;
      v.removeAttribute("aria-describedby");
      releaseVideoToPool(v);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);
  useLayoutEffect(() => {
    const v = video.current;
    if (!v) return;
    v.setAttribute("aria-label", tag || "Match video");
    if (description) v.setAttribute("aria-describedby", descriptionId);
    else v.removeAttribute("aria-describedby");
  }, [src, tag, description, descriptionId, video]);
  useLayoutEffect(() => {
    const v = video.current;
    if (!v) return;
    if (picture) v.poster = picture;
    else v.removeAttribute("poster");
    if (fullscreen) v.setAttribute("controlsList", "nofullscreen");
    else v.removeAttribute("controlsList");
  }, [picture, fullscreen, video]);
  useEffect(() => {
    // A newer clip interrupts this play(), and a browser may still refuse it; the controls play it then.
    if (autoPlay && src) video.current?.play().catch(() => {});
  }, [autoPlay, src, video]);
  useEffect(() => {
    const el = frame.current;
    if (!fullscreen || !el) return;
    const off = onFullChange(() => setFull(fullElement() === el));
    return () => {
      off();
      // a frame that leaves the page takes its full screen with it
      if (fullElement() === el) exitFull();
    };
  }, [fullscreen]);
  const toggleFull = () => {
    if (full) exitFull();
    else if (frame.current) enterFull(frame.current, video.current);
  };
  const start = () => {
    // in full screen the "Not a delivery?" sheet would open out of sight, behind the frame
    if (!onLongPress || full) return;
    timer.current = window.setTimeout(() => onLongPress(), 500);
  };
  const stop = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };
  return (
    <div
      ref={frame}
      className={`vid${real ? " real" : ""}${src ? " player" : ""}${full ? " full" : ""}${flagged ? " flagged" : ""}${selected ? " sel" : ""}`}
      style={{ height, ...style }}
      onClick={onClick}
      onContextMenu={onContextMenu && !full ? (e) => { e.preventDefault(); onContextMenu(); } : undefined}
      onPointerDown={start}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? tag || "Open video" : undefined}
      onKeyDown={onClick ? (e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onClick(); } } : undefined}
    >
      {description && <p className="sr-only" id={descriptionId} data-testid="delivery-summary">{description}</p>}
      {!src && picture ? <img className="pic" src={picture} alt="" loading="lazy" decoding="async" onError={() => setFailed(picture)} /> : null}
      {track && !real ? <i className="trk" /> : null}
      {ball && !real ? <i className="ball" style={{ left: `${ball[0]}%`, top: `${ball[1]}%` }} /> : null}
      {play ? <i className="play" /> : null}
      {tag ? <span className="tag">{tag}</span> : null}
      {tr ? <span className="tr">{tr}</span> : null}
      {fullscreen && src ? (
        // a mouse click leaves the focus where it was, or the space bar would press this button again
        <button type="button" className="fs" aria-label={full ? "Exit full screen" : "Full screen"} title={full ? "Exit full screen" : "Full screen"} onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.preventDefault()} onClick={toggleFull} data-testid="fullscreen">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d={full ? "M9 4v5H4M15 4v5h5M20 15h-5v5M4 15h5v5" : "M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5"} /></svg>
        </button>
      ) : null}
      {children}
    </div>
  );
}

const SKINS = ["#b58a66", "#8d6547", "#c69a78", "#7a553c", "#a57b5a", "#b8906c", "#9c6f4f", "#c39a79"];
const KITS = ["#e9e9e9", "#dcdcdc", "#efefef", "#e2e2e2", "#cfd6e6"];

/**
 * A player's picture: `src` is a crop the worker cut (the bowler in his run-in,
 * the striker at release; the tall cells of label-review's re-ID groups show
 * the same kind of picture), shown whole on a dark ground. Without one, or
 * when it fails to load, the silhouette placeholder.
 *
 * `fill`: the crop fills its box when trimming it to the box cannot cut the
 * player. The worker pads the player by at least 30% of their width on each
 * side and 6% of their height below (`person_crop_box`), so a crop up to 1.6x
 * as wide as the box, or 0.9x as tall, keeps the whole player; any other crop
 * stays whole on the dark ground.
 */
export function Face({ id, size = 44, height, radius = 6, src, style, css, fill }: { id?: string | undefined; size?: number | string; height?: number | string | undefined; radius?: number | string; src?: string | null | undefined; style?: CSSProperties | undefined; css?: boolean | undefined; fill?: boolean | undefined }) {
  const [failed, setFailed] = useState<string | null>(null);
  const [filled, setFilled] = useState<string | null>(null);
  const h = typeof id === "string" ? [...id].reduce((a, c) => a + c.charCodeAt(0), 0) : 0;
  // `css`: the stylesheet sizes it (the roster tile differs by device), so no inline width/height.
  const s: CSSProperties = css ? { ...style } : { width: size, height: height ?? size, borderRadius: radius, ...style };
  const pic = src && failed !== src ? src : null;
  const onLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const box = img.parentElement;
    if (!fill || !box || !img.naturalHeight || !box.clientHeight) return;
    const r = img.naturalWidth / img.naturalHeight / (box.clientWidth / box.clientHeight);
    if (r >= 0.9 && r <= 1.6) setFilled(pic);
  };
  return (
    <div className={`face${pic ? " pic" : ""}`} style={{ ...s, ["--skin" as string]: SKINS[h % SKINS.length], ["--kit" as string]: KITS[h % KITS.length] }}>
      {pic ? <img src={pic} alt="" loading="lazy" decoding="async" className={fill && filled === pic ? "fill" : undefined} onLoad={onLoad} onError={() => setFailed(pic)} /> : null}
    </div>
  );
}

const GRIP_RINGS = [2.3, 3.8, 5.3, 6.8];
const BLADE = "M11.1 8.5C11.1 9.7 9.8 9.9 9.8 11.1L9.8 22Q9.8 23 10.8 23L13.2 23Q14.2 23 14.2 22L14.2 11.1C14.2 9.9 12.9 9.7 12.9 8.5Z";
// The ball's seam: the arcs bow from pole to pole, so the lines meet at the rim as on a real ball.
const seam = (bow: number) => `M12 3A${bow} 9 0 0 1 12 21`;

/**
 * The roster tile's picture before a player is chosen: a wooden bat with a
 * black grip on the batsman tile, a red ball with its seam on the bowler tile.
 * Flat icons on the tile's own ground, no box behind them.
 */
export function RoleIcon({ role }: { role: "batsman" | "bowler" }) {
  return (
    <div className={`ri ${role}`}>
      {role === "batsman" ? (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <defs><clipPath id="ri-blade"><path d={BLADE} /></clipPath></defs>
          <g transform="rotate(45 12 12)">
            <rect x="11.1" y="1" width="1.8" height="7.6" rx=".9" fill="#171a20" />
            <g stroke="#3d424b" strokeWidth=".45">{GRIP_RINGS.map((y) => <line key={y} x1="11.1" x2="12.9" y1={y} y2={y + 0.5} />)}</g>
            <path d={BLADE} fill="#c0834a" />
            <rect x="12" y="8" width="2.4" height="15.5" fill="#a56c37" clipPath="url(#ri-blade)" />
            <path d="M11.2 9.4L12 12.4L12.8 9.4" fill="none" stroke="#7d4f26" strokeWidth=".5" />
          </g>
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <defs><clipPath id="ri-rim"><circle cx="12" cy="12" r="9" /></clipPath></defs>
          <circle cx="12" cy="12" r="9" fill="#d13434" />
          <g transform="rotate(40 12 12)" fill="none" stroke="#f7e8cb" clipPath="url(#ri-rim)">
            <path d={seam(0.4)} strokeWidth="1" />
            <path d={seam(4)} strokeWidth="1" />
            <path d={seam(2.2)} strokeWidth="2.6" strokeDasharray="0.7 1.5" />
          </g>
        </svg>
      )}
    </div>
  );
}

/** A bottom sheet on a phone, a centred dialog on a desktop. `className` sizes one sheet differently (`yt-help`). */
export function Sheet({ onClose, children, className }: { onClose: () => void; children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const opener = useRef(document.activeElement instanceof HTMLElement ? document.activeElement : null);
  const titleId = useId();
  const close = useRef(onClose); close.current = onClose;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const heading = el.querySelector('h1,h2,h3,[role="heading"]');
    if (heading) {
      if (!heading.id) heading.id = titleId;
      el.setAttribute("aria-labelledby", heading.id);
    } else el.setAttribute("aria-label", "Dialog");
    return activateModal(el, () => close.current(), opener.current);
  }, [titleId]);
  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <div ref={ref} className={className ? `sheet ${className}` : "sheet"} role="dialog" aria-modal="true" tabIndex={-1} style={{ outline: "none" }}>
        {children}
      </div>
    </>
  );
}

export function useToast(): [ReactNode, (msg: string) => void] {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(null), 2600);
    return () => clearTimeout(t);
  }, [msg]);
  return [msg ? <div className="toast" role="status">{msg}</div> : null, setMsg];
}

export function ErrorLine({ error }: { error: Error | null }) {
  if (!error) return null;
  return <p className="err mt10" role="alert">{error.message}</p>;
}
